import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Eye, EyeOff,
  QrCode, Download, Copy, Loader2, ExternalLink, MousePointerClick, BarChart3,
} from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { PLATFORM_META } from "./LinkTree";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LinkTreeManager() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [loading, setLoading] = useState(true);
  const [links, setLinks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: "", url: "", platform: "other" });
  const [saving, setSaving] = useState(false);
  const [qr, setQr] = useState(null);
  const [qrImg, setQrImg] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [range, setRange] = useState(30);
  const [qrStyle, setQrStyle] = useState("framed");

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/linktree/analytics?days=${range}`, auth)
      .then((res) => setAnalytics(res.data))
      .catch(() => {});
    // eslint-disable-next-line
  }, [range]);

  const fetchLinks = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/linktree/links`, auth);
      setLinks(res.data.links || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load links");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchQr = useCallback(async (style = "framed") => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/linktree/qr`, auth);
      setQr(res.data);
      const img = await axios.get(`${BACKEND_URL}/api/linktree/qr/image.png?style=${style}`, {
        ...auth, responseType: "blob",
      });
      setQrImg(URL.createObjectURL(new Blob([img.data], { type: "image/png" })));
    } catch (e) {
      toast.error("Failed to load QR code");
    }
  }, []);

  useEffect(() => {
    fetchLinks();
    return () => { if (qrImg) URL.revokeObjectURL(qrImg); };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchQr(qrStyle);
    // eslint-disable-next-line
  }, [qrStyle]);

  const openCreate = () => {
    setEditing(null);
    setForm({ label: "", url: "", platform: "other" });
    setDialogOpen(true);
  };

  const openEdit = (link) => {
    setEditing(link);
    setForm({ label: link.label, url: link.url, platform: link.platform || "other" });
    setDialogOpen(true);
  };

  const saveLink = async () => {
    if (!form.label.trim()) return toast.error("Label is required");
    if (!/^https?:\/\//i.test(form.url.trim())) return toast.error("URL must start with http:// or https://");
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${BACKEND_URL}/api/linktree/links/${editing.id}`, form, auth);
        toast.success("Link updated");
      } else {
        await axios.post(`${BACKEND_URL}/api/linktree/links`, form, auth);
        toast.success("Link added");
      }
      setDialogOpen(false);
      fetchLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (link) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/linktree/links/${link.id}`, { enabled: !link.enabled }, auth);
      fetchLinks();
    } catch (e) {
      toast.error("Toggle failed");
    }
  };

  const deleteLink = async (link) => {
    if (!window.confirm(`Delete "${link.label}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/linktree/links/${link.id}`, auth);
      toast.success("Link deleted");
      fetchLinks();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const move = async (index, dir) => {
    const next = [...links];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setLinks(next);
    try {
      await axios.post(`${BACKEND_URL}/api/linktree/reorder`, { ordered_ids: next.map((l) => l.id) }, auth);
    } catch (e) {
      toast.error("Reorder failed");
      fetchLinks();
    }
  };

  const downloadQr = () => {
    if (!qrImg) return;
    const a = document.createElement("a");
    a.href = qrImg;
    a.download = "boh-linktree-qr.png";
    a.click();
  };

  const copyUrl = () => {
    if (!qr) return;
    navigator.clipboard.writeText(qr.public_url);
    toast.success("Public URL copied");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate("/")}
            className="border-slate-600 text-slate-300" data-testid="linktree-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-white">Link Tree Manager</h1>
            <p className="text-slate-400 text-sm">Public social links page + branded QR code</p>
          </div>
          <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="linktree-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Link
          </Button>
        </div>

        {/* QR + public URL card */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <QrCode className="w-4 h-4 text-amber-500" /> Public Page & QR Code
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row gap-4 items-start">
            {qrImg ? (
              <img src={qrImg} alt="Link Tree QR" className="w-40 h-auto rounded-lg border border-slate-700 bg-white"
                data-testid="linktree-qr-img" />
            ) : (
              <div className="w-40 h-40 flex items-center justify-center text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            )}
            <div className="space-y-3 flex-1">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Public URL (no login needed)</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-amber-400 text-sm bg-slate-900/60 px-2 py-1 rounded" data-testid="linktree-public-url">
                    {qr?.public_url || "…"}
                  </code>
                  <Button size="sm" variant="outline" onClick={copyUrl}
                    className="border-slate-600 text-slate-300 h-7" data-testid="linktree-copy-url-btn">
                    <Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                  {qr && (
                    <a href={qr.public_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center text-xs text-slate-400 hover:text-amber-400">
                      <ExternalLink className="w-3 h-3 mr-1" /> Open
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex gap-1" data-testid="linktree-qr-style-toggle">
                  {[
                    ["framed", "Framed"],
                    ["plain", "Plain"],
                  ].map(([key, label]) => (
                    <button key={key} onClick={() => setQrStyle(key)}
                      data-testid={`linktree-qr-style-${key}`}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        qrStyle === key
                          ? "bg-amber-600 border-amber-500 text-white"
                          : "bg-slate-900/60 border-slate-700 text-slate-400 hover:text-white"
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>
                <Button size="sm" onClick={downloadQr} disabled={!qrImg}
                  className="bg-slate-700 hover:bg-slate-600 text-white" data-testid="linktree-qr-download-btn">
                  <Download className="w-4 h-4 mr-1" /> Download QR
                </Button>
                <span className="text-slate-400 text-sm" data-testid="linktree-qr-scans">
                  Scans: <span className="text-white font-semibold">{qr?.scan_count ?? 0}</span>
                </span>
              </div>
              <p className="text-slate-500 text-xs">
                Scans are tracked — print this QR on flyers, cards, or merch. It always points to the public page.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Click analytics */}
        <Card className="bg-slate-800/50 border-slate-700" data-testid="linktree-analytics-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-amber-500" /> Clicks Over Time
              </CardTitle>
              <div className="flex gap-1">
                {[7, 30, 90].map((d) => (
                  <button key={d} onClick={() => setRange(d)}
                    data-testid={`linktree-range-${d}`}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                      range === d
                        ? "bg-amber-600 border-amber-500 text-white"
                        : "bg-slate-900/60 border-slate-700 text-slate-400 hover:text-white"
                    }`}>
                    {d}d
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!analytics ? (
              <div className="flex justify-center py-8 text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : analytics.total_clicks === 0 && !analytics.total_scans ? (
              <p className="text-slate-500 text-sm py-6 text-center" data-testid="linktree-analytics-empty">
                No clicks recorded in the last {analytics.days} days yet. Clicks are tracked from now on —
                share the QR code and watch this chart fill in.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(analytics.totals)
                    .sort((a, b) => b[1] - a[1])
                    .map(([p, n]) => {
                      const meta = PLATFORM_META[p] || PLATFORM_META.other;
                      return (
                        <span key={p} data-testid={`linktree-total-${p}`}
                          className="inline-flex items-center gap-1.5 text-xs bg-slate-900/60 border border-slate-700 rounded-full px-2.5 py-1 text-slate-300">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />
                          {meta.label}: <span className="text-white font-semibold">{n}</span>
                        </span>
                      );
                    })}
                  <span className="inline-flex items-center gap-1.5 text-xs bg-slate-900/60 border border-amber-700 rounded-full px-2.5 py-1 text-slate-300"
                    data-testid="linktree-total-scans">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    QR Scans: <span className="text-white font-semibold">{analytics.total_scans || 0}</span>
                  </span>
                  <span className="inline-flex items-center text-xs bg-amber-900/40 border border-amber-800 rounded-full px-2.5 py-1 text-amber-300">
                    Total: <span className="text-white font-semibold ml-1">{analytics.total_clicks}</span>
                  </span>
                </div>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={analytics.series} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                      <XAxis dataKey="date" stroke="#64748b" fontSize={11}
                        tickFormatter={(d) => { const p = d.split("-"); return `${Number(p[1])}/${Number(p[2])}`; }} />
                      <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }}
                        labelStyle={{ color: "#e2e8f0" }}
                        formatter={(value, key) => [value, key === "scans" ? "QR Scans" : (PLATFORM_META[key] || PLATFORM_META.other).label]}
                      />
                      {Object.keys(analytics.totals).map((p) => (
                        <Bar key={p} dataKey={p} stackId="clicks"
                          fill={(PLATFORM_META[p] || PLATFORM_META.other).color} radius={[2, 2, 0, 0]} />
                      ))}
                      <Line type="monotone" dataKey="scans" stroke="#f59e0b" strokeWidth={2}
                        dot={{ r: 2, fill: "#f59e0b" }} activeDot={{ r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Links list */}
        {loading ? (
          <div className="flex justify-center py-12 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
          </div>
        ) : links.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="py-12 text-center text-slate-400" data-testid="linktree-links-empty">
              No links yet. Click "Add Link" to add your first social platform.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {links.map((l, i) => {
              const meta = PLATFORM_META[l.platform] || PLATFORM_META.other;
              const Icon = meta.Icon;
              return (
                <Card key={l.id} className={`bg-slate-800/50 border-slate-700 ${!l.enabled ? "opacity-50" : ""}`}
                  data-testid={`linktree-row-${i}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => move(i, -1)} disabled={i === 0}
                        className="text-slate-500 hover:text-amber-400 disabled:opacity-30"
                        data-testid={`linktree-up-${i}`}>
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => move(i, 1)} disabled={i === links.length - 1}
                        className="text-slate-500 hover:text-amber-400 disabled:opacity-30"
                        data-testid={`linktree-down-${i}`}>
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    </div>
                    <Icon className="w-6 h-6 shrink-0" style={{ color: meta.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold truncate">{l.label}</p>
                      <p className="text-slate-500 text-xs truncate">{l.url}</p>
                    </div>
                    <Badge className="bg-slate-900/60 text-slate-300 border-slate-600 hidden sm:inline-flex">
                      <MousePointerClick className="w-3 h-3 mr-1" /> {l.clicks || 0}
                    </Badge>
                    {!l.enabled && (
                      <Badge className="bg-amber-900/50 text-amber-300 border-amber-700">Hidden</Badge>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => toggleEnabled(l)}
                      className="text-slate-400 hover:text-white h-8 w-8" data-testid={`linktree-toggle-${i}`}>
                      {l.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(l)}
                      className="text-slate-400 hover:text-white h-8 w-8" data-testid={`linktree-edit-${i}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => deleteLink(l)}
                      className="text-red-400 hover:text-red-300 h-8 w-8" data-testid={`linktree-delete-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Link" : "Add Link"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Platform</Label>
              <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="linktree-platform-select">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 text-white border-slate-700">
                  {Object.entries(PLATFORM_META).map(([key, m]) => (
                    <SelectItem key={key} value={key} className="text-white hover:bg-slate-700">
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Label</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Follow us on Facebook"
                className="bg-slate-700 border-slate-600 text-white" data-testid="linktree-label-input" />
            </div>
            <div>
              <Label className="text-slate-300">URL</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://facebook.com/yourpage"
                className="bg-slate-700 border-slate-600 text-white" data-testid="linktree-url-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}
              className="border-slate-600 text-slate-300">Cancel</Button>
            <Button onClick={saveLink} disabled={saving}
              className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="linktree-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save Changes" : "Add Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
